import React, { useState, useEffect } from 'react';
import FleetHealthOverview from './FleetHealthOverview';
import FleetHealthInsightsPatterns from './FleetHealthInsightsPatterns';
import DeviceProfile from './DeviceProfile';
import { fetchFleetWidespreadIssues } from '../api/restapi';
import '../assets/AllTabsCommon.css';
import '../assets/FleetHealthTabs.css';

function toDateTimeLocal(value) {
    const candidate = value ? new Date(value) : new Date();
    const date = Number.isNaN(candidate.getTime()) ? new Date() : candidate;
    const pad = (part) => String(part).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function FleetHealthTabs({ userInfo, role, deviceProfileSystemId, deviceProfileContext, onDeviceProfileConsumed, onDeviceProfileContextConsumed, setNdStartDevice, setSelectedTab }) {
    const subTabs = [
        { key: 'Overview',         label: 'Health' },
        { key: 'InsightsPatterns', label: 'Health Insights' },
        { key: 'Device',           label: 'Device' },
    ];

    const [activeSubTab, setActiveSubTab] = useState(subTabs[0].key);
    const [deepLink, setDeepLink] = useState(null);
    const [deviceSystemId, setDeviceSystemId] = useState(null);
    const [deviceProfileLaunch, setDeviceProfileLaunch] = useState(null);
    const [pendingDeviceProfileTopScroll, setPendingDeviceProfileTopScroll] = useState(false);
    const [widespreadIssues, setWidespreadIssues] = useState(null);
    const deviceProfileTopRef = React.useRef(null);

    useEffect(() => {
        fetchFleetWidespreadIssues(7).then(data => {
            if (!data?.errorMessage) setWidespreadIssues(data);
        });
    }, []);

    const handleNavigateToSubTab = (tabKey, link) => {
        if (link) setDeepLink(link);
        setActiveSubTab(tabKey);
    };

    const handleNavigateToDevice = (systemId) => {
        // A normal Health Insights handoff must not inherit an earlier raw-event
        // timestamp/range and trigger a second automatic investigation.
        setDeviceProfileLaunch(null);
        setDeviceSystemId(systemId);
        setActiveSubTab('Device');
    };

    const handleNavigateToDeviceInvestigation = ({ systemId, referenceTimestamp, contextWindow }) => {
        setDeviceSystemId(systemId);
        setDeviceProfileLaunch({
            referenceTimestamp: toDateTimeLocal(referenceTimestamp),
            contextWindow: contextWindow || '± 12 hours',
        });
        setPendingDeviceProfileTopScroll(true);
        setActiveSubTab('Device');
    };

    React.useEffect(() => {
        if (!deviceProfileContext?.systemId) return;
        setDeviceSystemId(deviceProfileContext.systemId);
        setDeviceProfileLaunch({
            referenceTimestamp: toDateTimeLocal(deviceProfileContext.referenceTimestamp),
        });
        setPendingDeviceProfileTopScroll(true);
        setActiveSubTab('Device');
        onDeviceProfileContextConsumed && onDeviceProfileContextConsumed();
    }, [deviceProfileContext, onDeviceProfileContextConsumed]);

    // Raw Analytics markers reuse the Device Profile, so reset the document viewport
    // after the Device sub-tab and its investigation state have rendered instead of
    // preserving the Analytics scroll offset.
    React.useEffect(() => {
        if (!pendingDeviceProfileTopScroll || activeSubTab !== 'Device') return;
        let secondFrameId;
        const resetViewport = () => {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
            if (typeof deviceProfileTopRef.current?.scrollIntoView === 'function') {
                deviceProfileTopRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
            }
            setPendingDeviceProfileTopScroll(false);
        };
        const firstFrameId = window.requestAnimationFrame(() => {
            secondFrameId = window.requestAnimationFrame(resetViewport);
        });
        return () => {
            window.cancelAnimationFrame(firstFrameId);
            if (secondFrameId) window.cancelAnimationFrame(secondFrameId);
        };
    }, [activeSubTab, pendingDeviceProfileTopScroll]);

    // When App.js passes a systemId from an external Deep Dive link, switch to Device sub-tab
    React.useEffect(() => {
        if (typeof deviceProfileSystemId !== 'string' || !deviceProfileSystemId) return;
        setDeviceProfileLaunch(null);
        setDeviceSystemId(deviceProfileSystemId);
        setActiveSubTab('Device');
        onDeviceProfileConsumed && onDeviceProfileConsumed();
    }, [deviceProfileSystemId]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="fh-tabs-container">
            <div className="fh-sub-nav-wrap">
                <nav className="analytics-tabs">
                    {subTabs.map((tab) => (
                        <div
                            key={tab.key}
                            className={`analytics-tab${activeSubTab === tab.key ? ' active' : ''}`}
                            onClick={() => setActiveSubTab(tab.key)}
                        >
                            {tab.label}
                        </div>
                    ))}
                </nav>
            </div>

            {/* All sub-tabs always mounted, toggled via visibility */}
            <div style={{ display: activeSubTab === 'Overview' ? 'block' : 'none' }}>
                <div className="fh-sub-content">
                    <FleetHealthOverview
                        userInfo={userInfo}
                        role={role}
                        onNavigateToSubTab={handleNavigateToSubTab}
                        onNavigateToDeviceInvestigation={handleNavigateToDeviceInvestigation}
                    />
                </div>
            </div>
            <div style={{ display: activeSubTab === 'InsightsPatterns' ? 'block' : 'none' }}>
                <div className="fh-sub-content">
                    <FleetHealthInsightsPatterns
                        userInfo={userInfo}
                        role={role}
                        onNavigateToDeviceProfile={handleNavigateToDevice}
                        deepLink={deepLink}
                        onDeepLinkConsumed={() => setDeepLink(null)}
                        widespreadIssues={widespreadIssues}
                    />
                </div>
            </div>
            <div style={{ display: activeSubTab === 'Device' ? 'block' : 'none' }}>
                <div className="fh-sub-content" ref={deviceProfileTopRef}>
                    <DeviceProfile
                        userInfo={userInfo}
                        role={role}
                        initialSystemId={deviceSystemId}
                        initialReferenceTimestamp={deviceProfileLaunch?.referenceTimestamp}
                        initialContextWindow={deviceProfileLaunch?.contextWindow}
                        autoInvestigate={Boolean(deviceProfileLaunch)}
                        setNdStartDevice={setNdStartDevice}
                        setSelectedTab={setSelectedTab}
                    />
                </div>
            </div>
        </div>
    );
}

export default FleetHealthTabs;
